import { useCallback, useEffect, useState } from "react";
import * as WalletService from "features/wallet/api/Wallet";
import type { LiquidityPool } from "features/wallet/api/Wallet";
import { invalidateHoldingsCache } from "features/investments";
import { invalidateProfileStatsCache } from "lib/profileStatsCache";
import { responseCache } from "shared/money/responseCache";
import { apiGetSWR } from "utils/apiClient";

function poolsEndpoint(username: string): string {
    return `/wallet/${username}/pools`;
}

export interface UseWalletReturn {
    pools: LiquidityPool[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    createPool: (data: { name: string; currency: string; initialBalance?: number }) => Promise<{ success: boolean; error?: string }>;
    updatePool: (poolId: string, data: { name?: string; currency?: string }) => Promise<{ success: boolean; error?: string }>;
    deletePool: (poolId: string) => Promise<{ success: boolean; error?: string }>;
    transfer: (data: { sourcePoolId: string; targetPoolId: string; amount: number }) => Promise<{ success: boolean; error?: string }>;
    exchange: (data: {
        sourcePoolId: string;
        targetPoolId: string;
        sourceAmount: number;
        rate: number;
        rateMultiplies: boolean;
    }) => Promise<{ success: boolean; error?: string }>;
}

export function useWallet(username: string | null): UseWalletReturn {
    const [pools, setPools] = useState<LiquidityPool[]>(() => {
        if (!username) return [];
        const cached = responseCache.get<unknown>(poolsEndpoint(username));
        return Array.isArray(cached) ? (cached as LiquidityPool[]) : [];
    });
    const [loading, setLoading] = useState(() => {
        if (!username) return false;
        return responseCache.get<unknown>(poolsEndpoint(username)) === null;
    });
    const [error, setError] = useState<string | null>(null);

    const fetchPools = useCallback(async () => {
        if (!username) {
            setPools([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await WalletService.getPools(username);
            setPools(data);
        } catch (err) {
            setError(String(err));
            setPools([]);
        } finally {
            setLoading(false);
        }
    }, [username]);

    useEffect(() => {
        if (!username) {
            setPools([]);
            setLoading(false);
            return;
        }
        const endpoint = poolsEndpoint(username);
        setError(null);
        let cancelled = false;

        const { stale, done, cancel } = apiGetSWR<LiquidityPool[]>(endpoint, (fresh) => {
            setPools(Array.isArray(fresh) ? fresh : []);
            setError(null);
        }, undefined, { isCurrent: () => !cancelled });

        if (stale !== null && Array.isArray(stale)) {
            setPools(stale);
            setLoading(false);
        } else {
            setLoading(true);
        }

        void done
            .catch((err) => {
                if (cancelled) return;
                setError(String(err));
                setPools([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            cancel();
        };
    }, [username]);

    const createPool = useCallback(
        async (data: { name: string; currency: string; initialBalance?: number }) => {
            if (!username) return { success: false, error: "Not logged in" };
            try {
                const res = await WalletService.createPool(username, data);
                if ("message" in res && res.message) {
                    return { success: false, error: res.message };
                }
                await fetchPools();
                invalidateHoldingsCache(username);
                invalidateProfileStatsCache(username);
                return { success: true };
            } catch (err) {
                return { success: false, error: String(err) };
            }
        },
        [username, fetchPools]
    );

    const updatePool = useCallback(
        async (poolId: string, data: { name?: string; currency?: string }) => {
            if (!username) return { success: false, error: "Not logged in" };
            try {
                const res = await WalletService.updatePool(username, poolId, data);
                if ("message" in res && res.message) {
                    return { success: false, error: res.message };
                }
                await fetchPools();
                invalidateHoldingsCache(username);
                invalidateProfileStatsCache(username);
                return { success: true };
            } catch (err) {
                return { success: false, error: String(err) };
            }
        },
        [username, fetchPools]
    );

    const deletePool = useCallback(
        async (poolId: string) => {
            if (!username) return { success: false, error: "Not logged in" };
            try {
                const res = await WalletService.deletePool(username, poolId);
                if ("message" in res && res.message && !res.message.toLowerCase().includes("success")) {
                    return { success: false, error: res.message };
                }
                await fetchPools();
                invalidateHoldingsCache(username);
                invalidateProfileStatsCache(username);
                return { success: true };
            } catch (err) {
                return { success: false, error: String(err) };
            }
        },
        [username, fetchPools]
    );

    const transfer = useCallback(
        async (data: { sourcePoolId: string; targetPoolId: string; amount: number }) => {
            if (!username) return { success: false, error: "Not logged in" };
            try {
                const res = await WalletService.transfer(username, data);
                if ("message" in res && res.message) {
                    return { success: false, error: res.message };
                }
                await fetchPools();
                invalidateHoldingsCache(username);
                invalidateProfileStatsCache(username);
                return { success: true };
            } catch (err) {
                return { success: false, error: String(err) };
            }
        },
        [username, fetchPools]
    );

    const exchange = useCallback(
        async (data: {
            sourcePoolId: string;
            targetPoolId: string;
            sourceAmount: number;
            rate: number;
            rateMultiplies: boolean;
        }) => {
            if (!username) return { success: false, error: "Not logged in" };
            try {
                const res = await WalletService.exchange(username, data);
                if ("message" in res && res.message) {
                    return { success: false, error: res.message };
                }
                await fetchPools();
                invalidateHoldingsCache(username);
                invalidateProfileStatsCache(username);
                return { success: true };
            } catch (err) {
                return { success: false, error: String(err) };
            }
        },
        [username, fetchPools]
    );

    return {
        pools,
        loading,
        error,
        refetch: fetchPools,
        createPool,
        updatePool,
        deletePool,
        transfer,
        exchange,
    };
}
